import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Check, ChevronsUpDown, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * BusinessTypeCombobox — autocomplete + freeform field for the new
 * Customer "business_type" axis (Phase 6o Hotfix I).
 *
 * Behaviour
 * ─────────
 * - Always open as a Popover; surfaces three sources of suggestions
 *   merged & deduped:
 *     1. live values returned by GET /api/tenants/business-types
 *        (every value already used elsewhere in the org, with counts)
 *     2. a sensible starter list (i18n'd) so day-one operators don't
 *        face an empty combobox
 *     3. whatever the operator just typed (so they can commit a brand
 *        new value with one Enter / mouse-click)
 * - Free text is allowed — the combobox is not a strict select.
 * - Empty string ("") is the canonical "Uncategorised" value; we
 *   render a small "Clear" affordance for it instead of a checkbox.
 *
 * Props
 *   value: string | null
 *   onChange: (next: string) => void
 *   testid?: string
 *   placeholder?: string
 */
export function BusinessTypeCombobox({
  value,
  onChange,
  testid = "business-type-combobox",
  placeholder,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]); // [{value, count}]
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Phase 6o Hotfix I — starter list. Translation keys live under
  // `pages.customers.businessTypes.*`. Operators can ignore them and
  // type their own; the dropdown auto-suggests existing values across
  // the org so the second occurrence never has to type the full word.
  const starter = useMemo(
    () =>
      [
        "hotel",
        "restaurant",
        "realEstate",
        "medicalClinic",
        "lawFirm",
        "callCenter",
        "manufacturing",
        "retail",
        "education",
        "government",
        "techSaas",
        "other",
      ].map((k) => t(`pages.customers.businessTypes.${k}`)),
    [t]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/tenants/business-types")
      .then((r) => {
        if (!cancelled) setSuggestions(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // De-duplicate: existing values (with counts) win over starter list.
  const merged = useMemo(() => {
    const byVal = new Map();
    for (const row of suggestions) {
      if (!row.value) continue; // empty == "uncategorised"
      byVal.set(row.value.toLowerCase(), {
        value: row.value,
        count: row.count,
        source: "live",
      });
    }
    for (const s of starter) {
      const k = s.toLowerCase();
      if (!byVal.has(k)) {
        byVal.set(k, { value: s, count: 0, source: "starter" });
      }
    }
    const list = Array.from(byVal.values());
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      return list.filter((r) => r.value.toLowerCase().includes(q));
    }
    return list.sort((a, b) => {
      if ((b.count || 0) !== (a.count || 0)) return (b.count || 0) - (a.count || 0);
      return a.value.localeCompare(b.value);
    });
  }, [suggestions, starter, query]);

  // The pending value is whatever the operator has typed; if it does
  // not match any existing value we offer to "Add new value".
  const pendingNew = query.trim();
  const isExistingMatch = pendingNew
    ? merged.some((r) => r.value.toLowerCase() === pendingNew.toLowerCase())
    : true;

  const commit = (next) => {
    onChange(next);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testid}
        >
          <span className="flex items-center gap-2 truncate min-w-0">
            <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">
              {value
                ? value
                : placeholder ||
                  t("pages.customers.businessTypePlaceholder", "Select business type…")}
            </span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        data-testid={`${testid}-popover`}
      >
        <div className="border-b border-border p-2">
          <Input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pendingNew) {
                e.preventDefault();
                commit(pendingNew);
              }
              if (e.key === "Escape") setOpen(false);
            }}
            placeholder={t(
              "pages.customers.businessTypeSearch",
              "Type to filter or add new…"
            )}
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {value ? (
            <button
              type="button"
              className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
              onClick={() => commit("")}
              data-testid={`${testid}-clear`}
            >
              <span className="flex items-center gap-2">
                <X className="h-3 w-3" />
                {t("pages.customers.businessTypeUncategorise", "Clear (Uncategorised)")}
              </span>
            </button>
          ) : null}
          {loading && merged.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">…</div>
          ) : null}
          {merged.length === 0 && !loading && !pendingNew ? (
            <div className="px-3 py-2 text-xs text-muted-foreground italic">
              {t("pages.customers.businessTypeEmpty", "No suggestions yet.")}
            </div>
          ) : null}
          {merged.map((row) => {
            const selected = (value || "").toLowerCase() === row.value.toLowerCase();
            return (
              <button
                type="button"
                key={row.value}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/50 ${
                  selected ? "bg-muted/30 font-medium" : ""
                }`}
                onClick={() => commit(row.value)}
                data-testid={`${testid}-option-${row.value}`}
              >
                <span className="flex items-center gap-2 truncate">
                  {selected ? (
                    <Check className="h-3.5 w-3.5 text-[hsl(var(--accent))] shrink-0" />
                  ) : (
                    <span className="h-3.5 w-3.5 shrink-0" />
                  )}
                  <span className="truncate">{row.value}</span>
                </span>
                {row.count > 0 ? (
                  <span className="text-[10px] font-mono text-muted-foreground tabular-nums">
                    {row.count}
                  </span>
                ) : (
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                    {t("pages.customers.businessTypeSuggestion", "suggested")}
                  </span>
                )}
              </button>
            );
          })}
          {pendingNew && !isExistingMatch ? (
            <button
              type="button"
              className="flex w-full items-center justify-between px-3 py-1.5 text-sm hover:bg-[hsl(var(--accent))]/10 border-t border-border"
              onClick={() => commit(pendingNew)}
              data-testid={`${testid}-add-new`}
            >
              <span className="flex items-center gap-2 text-[hsl(var(--accent))]">
                <span className="h-3.5 w-3.5" />
                {t("pages.customers.businessTypeAddNew", 'Use new value: "{{val}}"', {
                  val: pendingNew,
                })}
              </span>
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
